import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom"; 
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Wifi, Server, ShieldCheck, GraduationCap, Crown, Briefcase, Loader2, Clock, BarChart3, RefreshCw } from "lucide-react"; 
import { Button } from "@/components/ui/button"; 
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner"; 
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => { 
  if (bytes === 0 || isNaN(bytes)) return '0 B'; 
  const k = 1024; 
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']; 
  const i = Math.floor(Math.log(bytes) / Math.log(k)); 
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]; 
};

// --- LOCAL UPTIME TICKER (Visuals Only) ---
const incrementUptime = (uptimeStr) => {
  if (!uptimeStr || uptimeStr === "0s" || uptimeStr === "-") return uptimeStr;
  let w = 0, d = 0, h = 0, m = 0, s = 0;
  const wMatch = uptimeStr.match(/(\d+)w/); if (wMatch) w = parseInt(wMatch[1]);
  const dMatch = uptimeStr.match(/(\d+)d/); if (dMatch) d = parseInt(dMatch[1]);
  const hMatch = uptimeStr.match(/(\d+)h/); if (hMatch) h = parseInt(hMatch[1]);
  const mMatch = uptimeStr.match(/(\d+)m/); if (mMatch) m = parseInt(mMatch[1]);
  const sMatch = uptimeStr.match(/(\d+)s/); if (sMatch) s = parseInt(sMatch[1]);
  s += 1;
  if (s >= 60) { s = 0; m += 1; }
  if (m >= 60) { m = 0; h += 1; }
  if (h >= 24) { h = 0; d += 1; }
  if (d >= 7) { d = 0; w += 1; }
  let res = "";
  if (w > 0) res += `${w}w`;
  if (d > 0) res += `${d}d`;
  if (h > 0) res += `${h}h`;
  if (m > 0) res += `${m}m`;
  res += `${s}s`;
  return res || "0s";
};

export default function Dashboard() {
  const navigate = useNavigate(); 
  
  const [stats, setStats] = useState({ 
    total_users: 0, active_users: 0, student_active: 0, employee_active: 0, vip_active: 0,
    connection_status: false, top_users: [], longest_sessions: [], is_syncing: false 
  });
  
  const [showReport, setShowReport] = useState(false); 
  
  // Inspection & History State
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Action States
  const [isBypassing, setIsBypassing] = useState(false);
  const [isUnbypassing, setIsUnbypassing] = useState(false);
  const [isKicking, setIsKicking] = useState(false);

  // Ref to hold the "Registered" count
  const registeredCountRef = useRef(0);

  // --- 1. INITIAL LOAD ---
  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
      if (registeredCountRef.current === 0) registeredCountRef.current = res.data.total_users;
    } catch (e) { console.error("Initial load failed"); }
  };

  // --- 2. LIVE BACKGROUND SYNC (3-Second Heartbeat) ---
  const triggerLiveUpdate = async () => {
      try {
          // Pulse the live sync (fast)
          await axios.post(`${API}/hotspot/sync-live`);
          // Fetch updated stats (includes is_syncing flag from server.py)
          const res = await axios.get(`${API}/dashboard/stats`);
          
          setStats(prev => ({
              ...res.data,
              // Only update registered count if sync is NOT running (prevents flickering)
              total_users: res.data.is_syncing ? (registeredCountRef.current || res.data.total_users) : res.data.total_users 
          }));

          // If sync finished, update the ref
          if (!res.data.is_syncing) {
             registeredCountRef.current = res.data.total_users;
          }

      } catch (e) {
          console.error("Live update skipped");
      }
  };

  useEffect(() => {
    fetchStats();
    
    // [INTEGRATION: 3000ms Pulse for CCR Router]
    const liveInterval = setInterval(triggerLiveUpdate, 3000);

    const tickerInterval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        longest_sessions: prev.longest_sessions.map(u => ({
          ...u,
          uptime: incrementUptime(u.uptime)
        }))
      }));
    }, 1000);

    const reportTimer = setTimeout(() => setShowReport(true), 1500);

    return () => {
      clearInterval(liveInterval);
      clearInterval(tickerInterval);
      clearTimeout(reportTimer);
    };
  }, []);

  // --- INSPECTION HANDLERS (For Charts & Lists) ---
  const handleOpenProfile = async (username) => {
    setProfileDialogOpen(true);
    setLoadingDetails(true);
    setUserDetails(null);
    setHistory([]);
    try {
      const detailRes = await axios.get(`${API}/hotspot/users/${username}/details`);
      setUserDetails(detailRes.data);
      const historyRes = await axios.get(`${API}/hotspot/users/${username}/history`);
      setHistory(historyRes.data);
    } catch (e) { 
      toast.error("Could not fetch details");
      setProfileDialogOpen(false);
    } finally { 
      setLoadingDetails(false); 
    }
  };

  // --- ACTIONS (Bypass/Kick) ---
  const handleBypassUser = async () => {
    if (!userDetails?.mac_address || userDetails.mac_address === "-") return toast.error("No active MAC.");
    setIsBypassing(true);
    try {
      await axios.post(`${API}/hotspot/users/bind`, { username: userDetails.username, mac_address: userDetails.mac_address });
      toast.success("Device Bypassed!");
      setUserDetails(prev => ({ ...prev, is_bypassed: true }));
      triggerLiveUpdate();
    } catch (e) { toast.error("Bypass failed"); } finally { setIsBypassing(false); }
  };

  const handleUnbypassUser = async () => {
    setIsUnbypassing(true);
    try {
      await axios.post(`${API}/hotspot/users/unbind`, { username: userDetails.username, mac_address: userDetails.mac_address });
      toast.success("Bypass Removed");
      setUserDetails(prev => ({ ...prev, is_bypassed: false, status: 'Offline' }));
      triggerLiveUpdate();
    } catch (e) { toast.error("Failed to remove."); } finally { setIsUnbypassing(false); }
  };

  const handleKickUser = async () => {
    setIsKicking(true);
    try { 
      await axios.post(`${API}/hotspot/users/${userDetails.username}/kick`); 
      toast.success("Kicked"); 
      handleOpenProfile(userDetails.username); 
      triggerLiveUpdate(); 
    } catch { toast.error("Kick failed"); } finally { setIsKicking(false); }
  };

  // --- [INTEGRATION: Navigation Handlers] ---
  const goToUsers = (filter) => {
    navigate(`/users?${filter}`);
  };

  return (
    <div className="p-6 space-y-6 bg-[#f8fafc] min-h-screen animate-in fade-in duration-700">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl border border-slate-200 shadow-sm gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#1a3d2e] uppercase tracking-tighter">TAU Hotspot Manager</h1>
          <div className="flex items-center gap-2 mt-1">
            {/* CONNECTION STATUS & SYNC INDICATOR */}
            <div className={`h-2 w-2 rounded-full ${stats.connection_status ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              {stats.connection_status ? 'Online' : 'System Offline'}
            </span>
            
            {/* NEW: LIVE SYNC BADGE (Only shows when Users page is syncing) */}
            {stats.is_syncing && (
                <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold border border-blue-100 flex items-center gap-1 animate-pulse">
                    <RefreshCw className="w-3 h-3 animate-spin" /> Database Update in Progress...
                </span>
            )}
          </div>
        </div>
        
        {/* REFRESH BUTTON REMOVED - NOW AUTOMATIC */}
        <div className="text-right">
             <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">System Status</p>
             <p className="text-sm font-bold text-slate-700">{stats.is_syncing ? "Syncing..." : "Monitoring Live"}</p>
        </div>
      </div>

      {/* METRICS ROW (Clickable) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Registered: No Action */}
        <MetricCard label="Registered" value={registeredCountRef.current || stats.total_users} icon={<Server />} color="text-slate-600" bg="bg-slate-50" />
        
        {/* Active: Go to Online */}
        <div onClick={() => goToUsers('status=online')} className="cursor-pointer transition-transform hover:scale-105 active:scale-95">
             <MetricCard label="Online Now" value={stats.active_users} icon={<Wifi />} color="text-emerald-600" bg="bg-emerald-50" />
        </div>

        {/* Students: Go to Student Profile + Online */}
        <div onClick={() => goToUsers('profile=uprof-student&status=online')} className="cursor-pointer transition-transform hover:scale-105 active:scale-95">
             <MetricCard label="Students" value={stats.student_active} icon={<GraduationCap />} color="text-blue-600" bg="bg-blue-50" />
        </div>

        {/* Employees: Go to Employee Profile + Online */}
        <div onClick={() => goToUsers('profile=uprof-employee&status=online')} className="cursor-pointer transition-transform hover:scale-105 active:scale-95">
             <MetricCard label="Employees" value={stats.employee_active} icon={<ShieldCheck />} color="text-amber-600" bg="bg-amber-50" />
        </div>

        {/* VIP: Go to VIP Profile + Online */}
        <div onClick={() => goToUsers('profile=vip&status=online')} className="cursor-pointer transition-transform hover:scale-105 active:scale-95">
             <MetricCard label="VIP Active" value={stats.vip_active} icon={<Crown />} color="text-purple-600" bg="bg-purple-50" />
        </div>
      </div>

      {/* PANELS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT: Bandwidth (Inspects User) */}
        <Card className="lg:col-span-2 border-none shadow-sm rounded-2xl bg-white overflow-hidden">
          <div className="bg-[#1a3d2e] p-4 text-white font-bold text-xs flex justify-between items-center">
            <div className="flex items-center gap-2 uppercase tracking-wider"><Activity className="w-4 h-4" /> Bandwidth Consumers</div>
            <span className="text-[10px] opacity-60">Session Total</span>
          </div>
          <CardContent className="p-6 h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_users} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="username" type="category" width={90} tick={{fontSize: 12, fontWeight: 700, fill: '#64748b'}} axisLine={false} tickLine={false} />
                <Tooltip cursor={{fill: '#f8fafc'}} formatter={(val) => [formatBytes(val), "Usage"]} />
                <Bar 
                    dataKey="total_bytes" 
                    fill="#10b981" 
                    radius={[0, 4, 4, 0]} 
                    barSize={24} 
                    // [INTEGRATION: Opens Inspection Modal]
                    onClick={(d) => handleOpenProfile(d.username)} 
                    className="cursor-pointer hover:opacity-80 transition-opacity" 
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* RIGHT: Uptime (Inspects User) */}
        <Card className="border-none shadow-sm rounded-2xl bg-white overflow-hidden">
          <div className="bg-slate-800 p-4 text-white font-bold text-xs flex justify-between items-center">
            <div className="flex items-center gap-2 uppercase tracking-wider"><Clock className="w-4 h-4 text-blue-400" /> Longest Sessions</div>
            <span className="text-[10px] opacity-60">Uptime Leaders</span>
          </div>
          <CardContent className="p-0">
             <div className="divide-y divide-slate-50">
                {stats.longest_sessions && stats.longest_sessions.length > 0 ? (
                  stats.longest_sessions.map((u, i) => (
                    // [INTEGRATION: Opens Inspection Modal]
                    <div key={i} onClick={() => handleOpenProfile(u.username)} className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer group transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                          {i + 1}
                        </div>
                        <span className="text-sm font-bold text-slate-700">{u.username}</span>
                      </div>
                      <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded w-24 text-right">
                        {u.uptime}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-slate-400 text-xs italic">
                    No active sessions recorded.
                  </div>
                )}
             </div>
          </CardContent>
        </Card>
      </div>

      {/* POWER BI (KEPT AS IS) */}
      <div className="pt-4 pb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-700 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" /> Advanced Analytics
          </h3>
          <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">Live Report</span>
        </div>

        {showReport ? (
          <Card className="shadow-xl border-none overflow-hidden rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="w-full relative aspect-[21/9] bg-slate-900 flex items-center justify-center">
                 <iframe 
                   title="TAU Network Report" 
                   className="absolute inset-0 w-full h-full border-0" 
                   src="https://app.powerbi.com/view?r=eyJrIjoiN2MzNTg1YmYtMjI4ZS00MjY2LTlhMTUtODQ5MjBmN2ZlYWE3IiwidCI6IjAwM2MxODdjLWM1NjAtNDY2Yi1iNjVlLWUxMjNkZTBjZjExNiIsImMiOjEwfQ%3D%3D" 
                   allowFullScreen={true} 
                 />
              </div>
          </Card>
        ) : (
          <div className="h-[120px] w-full bg-slate-100 rounded-2xl flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mb-2 text-emerald-500/50" />
              <p className="text-xs font-bold uppercase tracking-widest">Initializing Analytics Engine...</p>
          </div>
        )}
      </div>

      {/* INSPECTION MODAL (KEPT AS IS) */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="p-0 border-0 shadow-2xl max-w-md overflow-hidden">
          <div className="bg-[#1a3d2e] p-8 text-white relative">
            <h3 className="text-2xl font-black">{userDetails?.username || "---"}</h3>
            <div className="flex items-center gap-2 mt-2">
                <span className={`inline-block w-2 h-2 rounded-full ${userDetails?.status === 'Online' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`}></span>
                <p className="opacity-80 font-medium text-sm">{userDetails?.status || "Checking..."}</p>
            </div>
          </div>
          
          <div className="p-6 bg-white space-y-4">
            {loadingDetails ? <div className="py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div> : (
              <>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-4 bg-emerald-50 rounded-xl font-bold border border-emerald-100">
                    <p className="text-[10px] text-emerald-600 uppercase mb-1">Total Usage</p>
                    <p className="text-lg text-emerald-900">{formatBytes(userDetails?.total_bytes)}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl font-bold border border-slate-100">
                    <p className="text-[10px] text-slate-400 uppercase mb-1">Uptime</p>
                    <p className="text-lg text-slate-700">{userDetails?.uptime || '0s'}</p>
                  </div>
                </div>

                <div className="space-y-1 bg-slate-50 p-4 rounded-xl text-sm border border-slate-100">
                   <div className="flex justify-between">
                      <span className="text-slate-400 font-medium flex gap-2"><Briefcase className="w-4 h-4"/> MAC</span>
                      <span className="font-mono font-bold text-slate-700">{userDetails?.mac_address || "-"}</span>
                   </div>
                   <div className="flex justify-between mt-2">
                      <span className="text-slate-400 font-medium flex gap-2"><Activity className="w-4 h-4"/> IP</span>
                      <span className="font-mono font-bold text-slate-700">{userDetails?.ip_address || "-"}</span>
                   </div>
                </div>

                <div className="space-y-2 pt-2">
                    {userDetails?.is_bypassed ? (
                      <Button onClick={handleUnbypassUser} disabled={isUnbypassing} variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-bold h-11">
                        {isUnbypassing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />} 
                        Remove Bypass (Force Login)
                      </Button>
                    ) : (
                      <Button onClick={handleBypassUser} disabled={isBypassing} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11">
                        {isBypassing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} 
                        Bypass Login (IP Bind)
                      </Button>
                    )}
                    
                    {userDetails?.status === 'Online' && !userDetails?.is_bypassed && (
                      <Button onClick={handleKickUser} disabled={isKicking} variant="destructive" className="w-full bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white transition-colors h-11">
                          {isKicking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                          Disconnect Session
                      </Button>
                    )}
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> Recent Session History
                  </p>
                  <div className="space-y-3">
                    {history.length > 0 ? history.map((h, i) => (
                      <div key={i} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-bold text-slate-700">{h.date}</p>
                          <p className="text-slate-400">{h.duration} duration</p>
                        </div>
                        <span className="font-mono text-emerald-600 font-bold">{formatBytes(h.data)}</span>
                      </div>
                    )) : (
                      <p className="text-center text-[10px] text-slate-400 italic py-2">No previous records found.</p>
                    )}
                  </div>
                </div>

              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value, icon, color, bg }) {
  return (
    <Card className="border-none shadow-sm rounded-2xl bg-white group overflow-hidden">
      <CardContent className="p-6 flex justify-between items-center">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
          <h3 className={`text-3xl font-black ${color}`}>{value?.toLocaleString() || 0}</h3>
        </div>
        <div className={`p-4 rounded-2xl ${bg} ${color}`}>{icon}</div>
      </CardContent>
    </Card>
  );
}